import React from "react";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

/*
 * The views reached from the Forms menu rather than the tab strip. Leaving a
 * form returns to the tab it was opened from, so a form is never the thing
 * that is returned to.
 */
const FORM_VIEW_TYPES = new Set([
  "discharge-summary",
  "referral-form",
  "death-summary",
  "investigation-referral-form",
  "review-forms",
  "emergency-shift-form",
  "diet-discharge-summary",
  "nutritionist-reassessment",
  "emr-download",
]);

const isFormType = (type) =>
  typeof type === "string" &&
  (FORM_VIEW_TYPES.has(type) || type.startsWith("clinical-form-"));

const TAB_MEMORY_KEY = "curengo:lastPatientTab";

/**
 * Remembers the tab a form was opened from.
 *
 * The tab strip navigates with `replace`, so there is no history entry to go
 * back to - the step before the form has to be written down as it happens.
 * Held in sessionStorage rather than React state because the form that reads
 * it is mounted after the tab that set it has gone.
 */
export const rememberPatientTab = (type) => {
  if (!type || isFormType(type)) return;
  try {
    window.sessionStorage.setItem(TAB_MEMORY_KEY, type);
  } catch {
    /* A browser refusing storage is not a reason to fail the screen. */
  }
};

/**
 * The way out of a form's editor.
 *
 * Every form opens its editor over whatever was on screen - a saved document,
 * or nothing at all the first time one is raised - and until now only some of
 * them offered a way back. Where there is a saved form it is put back; where
 * there is not, the form is left for the step it was opened from.
 *
 * `ia` is the fallback only: it is the one landing every role's own component
 * understands, for a form opened straight from a link with no step behind it.
 */
export const useLeavePatientForm = () => {
  const navigate = useNavigate();
  return () => {
    let back = null;
    try {
      back = window.sessionStorage.getItem(TAB_MEMORY_KEY);
    } catch {
      back = null;
    }
    const next = new URL(window.location.href);
    next.searchParams.set("type", isFormType(back) || !back ? "ia" : back);
    next.searchParams.delete("chat");
    navigate(`${window.location.pathname}?${next.searchParams.toString()}`);
  };
};

/**
 * @param onClose   what leaving does - restore the saved document, or leave
 * @param isDirty   boolean, or a function read at the moment of the click so
 *                  the answer is current rather than whatever it was on the
 *                  last render
 */
const FormCloseAction = ({ onClose, isDirty, label = "Close this form" }) => {
  const leave = () => {
    const dirty = typeof isDirty === "function" ? isDirty() : Boolean(isDirty);
    if (!dirty) {
      onClose?.();
      return;
    }
    Modal.confirm({
      title: "Leave without saving?",
      content:
        "This form has changes that have not been saved. Leaving now discards them.",
      okText: "Discard changes",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: () => onClose?.(),
    });
  };

  return (
    <CloseOutlined
      onClick={leave}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          leave();
        }
      }}
      title={label}
      aria-label={label}
    />
  );
};

export default FormCloseAction;
